import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { CollectionResponse, Video } from "@/api";
import {
  Button,
  ContentThumbnail,
  Description,
  PageLoader,
  Skeleton,
} from "@/components";
import { Completed, DataIcon, Incomplete } from "@/icons";
import { useScrollPosition, useTranslate, useUser } from "@/providers/context";
import { cn, getContentThumbnailUrl } from "@/utils";

import { useNavigateToItem } from "@/hooks/useNavigateToItem";
import { CourseOverview } from "../CourseOverview";
import { useCourseCompletion } from "../hooks/useCourseCompletion";

interface CourseViewProps {
  collection: CollectionResponse;
}

const Separator = () => {
  return (
    <div className="my-12 h-[1px] w-full border-t-[1px] border-solid border-border" />
  );
};

const Heading = ({
  title,
  className,
}: {
  title: string;
  className?: string;
}) => {
  return <h2 className={cn("h4-extrabold", className)}>{title}</h2>;
};

export const CourseView = ({ collection }: CourseViewProps) => {
  const { translate } = useTranslate();
  const { uid } = useUser();
  const { navigateToItem } = useNavigateToItem();
  const { getScrollPosition } = useScrollPosition();
  const { pathname } = useLocation();

  const { data: courseCompletion, isLoading } = useCourseCompletion(
    collection.TabId,
  );

  // Scroll to saved position if there is one
  // Need to use a timeout for CourseView so it scrolls once the Collapse components have opened
  useEffect(() => {
    const scrollContainer = document.getElementById("ScrollContainer--Content");

    if (!scrollContainer) {
      return;
    }

    const savedScrollPosition = getScrollPosition(pathname);

    if (savedScrollPosition) {
      const timeout = setTimeout(() => {
        scrollContainer.scrollTo(0, savedScrollPosition);
      }, 50);

      return () => clearTimeout(timeout);
    }
  }, [pathname, getScrollPosition]);

  const numLessons = Object.keys(collection.ChildVideos ?? {})
    .length.toString()
    .padStart(2, "0");

  const fullItem = courseCompletion?.next_video;
  const nextVideoNumber = useMemo(() => {
    if (!courseCompletion) return undefined;
    return Math.round(
      (courseCompletion.percentage_completion *
        courseCompletion.course_video_count) /
        100,
    );
  }, [courseCompletion]);

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-160px)] w-full items-center justify-center">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className=" w-[800px]">
        <ContentThumbnail
          thumbnail={
            collection.Thumbnails["large"] ??
            collection.Thumbnails["source"] ??
            ""
          }
          className="w-full rounded-[12px]"
        />
        <Heading title={collection.Name} className="pt-[36px]" />
        <h3 className="p2-semibold flex items-center pt-[12px] text-item-secondary">
          <DataIcon className="mr-1" />
          {numLessons} {translate("Lessons")}
          {!courseCompletion && !!uid ? (
            // Skeleton loading state
            <span className="flex items-center pl-4">
              <Skeleton className="mr-1 h-5 w-5 rounded-full" />
              <Skeleton className="h-4 w-20 rounded" />
            </span>
          ) : courseCompletion?.percentage_completion === 100 ? (
            <span className="flex items-center">
              <Completed className="ml-4 mr-1 h-5 w-5" />
              <span className="text-success-item">
                {translate("Completed")}
              </span>
            </span>
          ) : courseCompletion?.percentage_completion !== undefined ? (
            <span className="flex items-center pl-4">
              <Incomplete className="mr-1 h-5 w-5 text-item-secondary" />
              <span className="text-item-secondary">
                {courseCompletion?.percentage_completion}%{" "}
                {translate("Complete")}
              </span>
            </span>
          ) : null}
        </h3>
        <Separator />
        {!!courseCompletion && courseCompletion?.next_video && (
          <>
            <Heading title={translate("Continue Watching")} className="pb-5" />
            <div className="flex items-center">
              <ContentThumbnail
                thumbnail={
                  fullItem ? getContentThumbnailUrl(fullItem, "medium") : ""
                }
                contentItem={fullItem}
                className="h-[151px] w-[270px] rounded-[8px]"
              />
              <div className="flex flex-col px-6 text-item-secondary">
                <div className="p1-medium text-item-secondary pb-3">
                  {translate("Lesson")}{" "}{nextVideoNumber?.toString().padStart(2, "0")}{" "}
                  {translate("of")}{" "}
                  {courseCompletion?.course_video_count
                    .toString()
                    .padStart(2, "0")}
                </div>
                <div className="p1-bold pb-5 text-item-primary">
                  {fullItem?.Title}
                </div>
                <Button
                  variant="primary"
                  className="caption-semibold w-fit"
                  onClick={() => {
                    if (!fullItem) return;
                    navigateToItem({
                      parentId: courseCompletion.next_video_parent_id,
                      courseId: collection.TabId,
                      item: {
                        ChildId: fullItem?.VideoId.toString(),
                        Type: "video",
                      },
                      fullItem: fullItem as Video,
                    });
                  }}
                >
                  {translate("Continue")}
                </Button>
              </div>
            </div>
            <Separator />
          </>
        )}
        {collection.Description?.trim() ? (
          <>
            <Heading title={translate("About")} />
            <div className="p2-regular pt-3">
              <Description desc={collection.Description} />
            </div>
            <Separator />
          </>
        ) : null}
        <CourseOverview collection={collection} />
      </div>
    </div>
  );
};
